/**
 * Compoent library
 * extend an element with some compnent class
 *
 * @author Haithem Bel Haj (hb@dietaikonauten.com)
 */

//imports
import {toArray, uniqueId} from 'lodash';
import {removeListeners} from './event';

const docEl = document.documentElement;
let observer = {
  unobserve: function () {
  }
};

// Collection of Components
export const components = {};

/**
 * binds a class with definition
 *
 * @param Class
 * @param el
 * @param args
 * @returns {*}
 */
export const bind = function bind(Class, el, ...args) {

  const hiddenProp = getHiddenProp(Class);

  if (el[hiddenProp]) {

    return false;
  }

  el[hiddenProp] = new Class(el, ...args);

  return el[hiddenProp];
};

/**
 * Binds a component to a selector and saves a reference
 *
 * @param Class
 * @param selector
 * @param root
 */
export const register = function register(Class, selector, root = docEl) {

  if (!Class) {

    return;
  }

  // add unique id to class
  if (!Class._id) {

    Class._id = uniqueId();
  }

  const hash = getUniqueHash(Class, selector);
  const els = toArray(root.querySelectorAll(selector));
  const selectorsToInitializeRightAway = [
    '.tc-add',
    '.tc-overlay',
    '.tc-quickview-button',
    '.tc-overlay, .tc-quickview-button',
    '.tc-lazy-loading-image, .tc-blurry-background-image'
  ];

  if (window.IntersectionObserver) {
    observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            bind(Class, entry.target);
          }, {
            timeout: 5000
          });
        } else {
          bind(Class, entry.target);
        }
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: '100% 25% 100% 25%',
      threshold: 1
    });

    els.filter(el => {
      if (
        !el.getAttribute('data-load') &&
        !el.querySelector('[data-load]') &&
        selectorsToInitializeRightAway.indexOf(selector) === -1
      ) {
        observer.observe(el);
      } else {
        bind(Class, el);
      }
    });
  } else {
    els.filter(el => bind(Class, el));
  }

  if (!components[hash]) {

    // save a reference
    return (components[hash] = {selector, els, Class});
  }

  return (components[hash].els = components[hash].els.concat(els));
};

/**
 * reload all components
 *
 * @param root
 */
export const reload = function reload(root = docEl) {

  Object.keys(components).forEach(key => {

    const {Class, selector, els} = components[key];

    components[key].els = els.filter(el => !destroy(Class, el));

    register(Class, selector, root);
  });
};

/**
 * reload a specific component
 *
 * @param reloadClass
 */
export const reloadModule = function reloadModule(reloadClass) {

  Object.keys(components).forEach(key => {

    const {Class, selector, els} = components[key];

    if (reloadClass === Class) {

      els.filter(el => {

        !destroy(Class, el);
      });

      register(Class, selector);
    }
  });
};

/**
 * destroy a component
 *
 * @param Class
 * @param el
 */
export const destroy = function destroy(Class, el) {

  if (isInDOMTree(el)) {

    return false;
  }

  const instance = el[getHiddenProp(Class)];

  instance && instance.destroy && instance.destroy();

  removeListeners(el);
  deleteInstance(Class, el);

  return true;
};

/**
 * retrieve the instance of Class from an element
 *
 * @param Class
 * @param el
 * @returns {*}
 */
export const getInstance = function getInstance(Class, el) {
  const instance = el[getHiddenProp(Class)];

  if (instance) {
    return instance;
  }

  bind(Class, el);
  observer.unobserve(el);
  return el[getHiddenProp(Class)];
};

/**
 * retrieve the instance of Class from an element
 *
 * @param Class
 * @param el
 */
export const deleteInstance = function deleteInstance(Class, el) {

  delete el[getHiddenProp(Class)];
};

/**
 * test if elem is in dom tree
 *
 * @param node
 * @returns {boolean}
 */
export const isInDOMTree = function isInDOMTree(node) {

  return docEl.contains(node);
};

/**
 * the DOM ready callback
 *
 * @param callback
 * @returns {*}
 */
export const ready = function ready(callback) {

  if (document.readyState !== 'loading') {

    return callback();
  }

  document.addEventListener('DOMContentLoaded', callback);
};

export default {
  register,
  reload,
  reloadModule,
  destroy,
  bind,
  getInstance,
  deleteInstance,
  isInDOMTree,
  ready
};

/**
 * parse the attributes of an element given a schema
 *
 * example:
 *
 * Given this schema {id: Number, isClosed: Boolean}
 * And this el <div data-id="123456" data-is-closed="true"></div>
 * Will produce {id: 123456, isClosed: true}
 *
 * For special cases where you need more transformation
 * Given this schema {'title->content': String}
 * and this el <div data-title="some content"></div>
 * will produce {content: "some content"}
 *
 * @param schema
 * @param el
 * @param options
 * @returns {{}}
 */
// export function parseSchema(schema, el, options) {

//   const { prefix } = extend({ prefix: 'data-' }, options);

//   const out = {};

//   Object.keys(schema).forEach(key => {

//     const [attrKey, objKey] = key.split('->');

//     let attr = el.getAttribute(prefix + snake_case(attrKey));

//     if (attr !== null) {

//       return out[objKey || attrKey] = parse(schema[key], attr);
//     }
//   });

//   return out;
// }


/******************
 * helper functions
 *****************/

/**
 * get unique hash for a class
 *
 * @param Class
 * @param selector
 * @returns {*}
 */
function getUniqueHash(Class, selector) {

  return getHiddenProp(Class) + selector.trim();
}

/**
 * get the hidden property
 *
 * @param Class
 * @returns {*}
 */
function getHiddenProp(Class) {

  return `__${Class._id}__`;
}

/**
 * parse attr value given a type
 *
 * @param type
 * @param value
 * @returns {*}
 */
// function parse(type, value) {
//   if (isFunction(type)) {
//     return type(value);
//   }
//   switch (type) {
//   case Number:
//     return Number(value);
//   case String:
//     return value;
//   case Object:
//     return JSON.parse(value);
//   case Boolean:
//     return (value === 'true');
//   }
// }

/**
 * snake_case
 *
 * @param name
 * @param separator
 * @returns {*}
 */
// function snake_case(name, separator = '-') {

//   return name.replace(/[A-Z]/g, (letter, pos) => (pos ? separator : '') + letter.toLowerCase());
// }
